export interface FetchWithRetryOptions extends RequestInit {
  maxRetries?: number;
  retryDelay?: number; // base delay in ms
  backoffFactor?: number;
  timeout?: number; // timeout in ms
  retryOnStatus?: number[];
  onRetry?: (attempt: number, error: any) => void;
}

export class FetchError extends Error {
  status?: number;
  statusText?: string;
  data?: any;
  isTimeout?: boolean;

  constructor(message: string, status?: number, statusText?: string, data?: any, isTimeout = false) {
    super(message);
    this.name = "FetchError";
    this.status = status;
    this.statusText = statusText;
    this.data = data;
    this.isTimeout = isTimeout;
  }
}

/**
 * Robust fetch wrapper with automatic exponential backoff retries for transient errors,
 * timeout support, safe JSON / text response parsing, and error prevention.
 */
export async function fetchWithRetry(
  url: string,
  options: FetchWithRetryOptions = {}
): Promise<Response> {
  const {
    maxRetries = 3,
    retryDelay = 500,
    backoffFactor = 2,
    timeout = 15000,
    retryOnStatus = [429, 500, 502, 503, 504],
    onRetry,
    signal: userSignal,
    ...fetchOptions
  } = options;

  let attempt = 0;

  while (true) {
    attempt++;
    const controller = new AbortController();
    let isTimedOut = false;

    const timeoutId = timeout
      ? setTimeout(() => {
          isTimedOut = true;
          controller.abort();
        }, timeout)
      : null;

    // Combine user signal with timeout controller signal if user provided signal
    let effectiveSignal = controller.signal;
    if (userSignal) {
      if (userSignal.aborted) {
        controller.abort();
      } else {
        userSignal.addEventListener("abort", () => controller.abort(), { once: true });
      }
    }

    try {
      const response = await fetch(url, {
        ...fetchOptions,
        signal: effectiveSignal,
      });

      if (timeoutId) clearTimeout(timeoutId);

      // If response is OK or status is not in the retryable status list, return response directly
      if (response.ok || !retryOnStatus.includes(response.status)) {
        return response;
      }

      // If status is retryable and we have remaining retries
      if (attempt <= maxRetries) {
        if (onRetry) {
          onRetry(attempt, new FetchError(`HTTP ${response.status}`, response.status, response.statusText));
        }
        const jitter = Math.random() * 100;
        const delay = retryDelay * Math.pow(backoffFactor, attempt - 1) + jitter;
        await new Promise((res) => setTimeout(res, delay));
        continue;
      }

      return response;
    } catch (err: any) {
      if (timeoutId) clearTimeout(timeoutId);

      const isAbortErr = err.name === "AbortError" || isTimedOut;
      const isNetworkErr = !isAbortErr || isTimedOut;

      if (attempt <= maxRetries && isNetworkErr) {
        if (onRetry) {
          onRetry(attempt, err);
        }
        const jitter = Math.random() * 100;
        const delay = retryDelay * Math.pow(backoffFactor, attempt - 1) + jitter;
        await new Promise((res) => setTimeout(res, delay));
        continue;
      }

      if (isTimedOut) {
        throw new FetchError(`Request timed out after ${timeout}ms`, 408, "Request Timeout", null, true);
      }
      throw err;
    }
  }
}

/**
 * Helper to execute fetchWithRetry and safely parse JSON or text response.
 * Handles HTTP 204 (No Content), empty response bodies, and HTML fallback error messages cleanly.
 */
export async function fetchJsonWithRetry<T = any>(
  url: string,
  options: FetchWithRetryOptions = {}
): Promise<T> {
  const response = await fetchWithRetry(url, options);

  if (response.status === 204) {
    return null as unknown as T;
  }

  const contentType = response.headers.get("Content-Type") || "";
  const rawText = await response.text();

  let parsedData: any = null;
  if (rawText && rawText.trim().length > 0) {
    if (contentType.includes("application/json") || rawText.trim().startsWith("{") || rawText.trim().startsWith("[")) {
      try {
        parsedData = JSON.parse(rawText);
      } catch (jsonErr) {
        parsedData = { rawText, parseError: "Invalid JSON response" };
      }
    } else {
      parsedData = rawText;
    }
  }

  if (!response.ok) {
    let errorMsg = `Request failed with status ${response.status}`;
    if (parsedData && typeof parsedData === "object") {
      if (typeof parsedData.message === "string" && parsedData.message.trim()) {
        errorMsg = parsedData.message.trim();
      } else if (parsedData.error) {
        errorMsg = typeof parsedData.error === "string" ? parsedData.error : JSON.stringify(parsedData.error);
      }
    } else if (typeof parsedData === "string") {
      errorMsg = parsedData.length > 200 ? parsedData.substring(0, 200) + "..." : parsedData;
    }

    throw new FetchError(errorMsg, response.status, response.statusText, parsedData);
  }

  return parsedData as T;
}

export default fetchWithRetry;
